import { zodResolver } from '@hookform/resolvers/zod';
import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import Modal from '~/components/Modal/Modal';
import {
  ModalBody,
  ModalFooter,
  ModalHeader,
} from '~/components/ModalComponents';
import { checkoutStore, mainStore, userStore } from '~/stores';
import { createNameValidation } from '~/utils/validators';

import { useModal } from '../../hooks/useModal';

import InputText from '../InputText/InputText';

import styles from './ModalCheckoutRecieverCD.module.scss';

const ModalCheckoutRecieverCD = () => {
  const { t } = useTranslation();
  const { closeModal } = useModal();
  const [serverError, setServerError] = useState<string | null>(null);

  const backHandler = () => closeModal();

  useEffect(() => {
    setValue('name', checkoutStore.nameVal ?? '');
    setValue('email', checkoutStore.emailVal ?? '');
  }, []);

  const RecieverValidationSchema = z.object({
    name: createNameValidation(t).default(''),

    email: z
      .string()
      .min(1, { message: t('pleaseEnterEmail') })
      .email({ message: t('incorrectEmail') })
      .default(''),
  });

  type RecieverSchema = z.infer<typeof RecieverValidationSchema>;

  useEffect(() => {
    setValue('name', checkoutStore.nameVal ?? '');
    setValue('email', checkoutStore.emailVal ?? '');
  }, [checkoutStore.nameVal, checkoutStore.emailVal]);

  const handleForm = async (data: RecieverSchema) => {
    setServerError(null);
    const emailChanged = await userStore.updatePersonalEmail(data.email.trim());
    checkoutStore.setIsEmailSync(emailChanged);
    checkoutStore.setIsPersonalDataSync(true);

    if (!emailChanged) {
      return;
    }

    try {
      const { firstName, middleName, lastName } = userStore.splitFullName(
        data.name.trim(),
      );
      await userStore.updatePersonalData({
        first_name: firstName,
        middle_name: middleName,
        last_name: lastName,
      });

      checkoutStore.setNameVal(data.name);
      userStore.setPersonalDataName(data.name);
      mainStore.pushAlert('success', t('emailChanged'));

      closeModal();
    } catch (errorKey) {
      if (typeof errorKey === 'string') {
        setServerError(t(errorKey));
      }
    } finally {
      checkoutStore.setIsPersonalDataSync(true);
    }
  };

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<RecieverSchema>({
    resolver: zodResolver(RecieverValidationSchema),
  });

  const resetName = () => setValue('name', '');
  const resetEmail = () => setValue('email', '');

  const firstNameProps = register('name');
  const emailProps = register('email');

  const [nameVal, emailVal] = watch(['name', 'email']);

  return (
    <Modal
      isShow={true}
      className={styles.recieverForm}
      onBackdropDismiss={backHandler}
    >
      <form
        className={styles.recieverForm__container}
        onSubmit={handleSubmit(handleForm)}
      >
        <ModalHeader
          title={t('reciever')}
          onClose={backHandler}
          className={styles.header}
        />
        <ModalBody className={styles.recieverForm__container__body}>
          <div className={styles.content}>
            <InputText
              classNameWrapper={styles.input_wrapper}
              classNameInput={styles.input}
              classNameLabelError={styles.labelError}
              classNameInputError={styles.inputError}
              placeholder={t('messageNamePlaceholder')}
              externalLabel={t('name')}
              error={errors.name?.message || serverError || ''}
              refObj={firstNameProps.ref}
              handleReset={nameVal?.length > 0 ? resetName : undefined}
              required
              {...firstNameProps}
              onChange={(e) => {
                setServerError(null);
                firstNameProps.onChange(e);
              }}
            />
            <InputText
              classNameWrapper={styles.input_wrapper}
              classNameInput={styles.input}
              classNameLabelError={styles.labelError}
              classNameInputError={styles.inputError}
              placeholder={t('messageEmailPlaceholder')}
              externalLabel={t('email')}
              error={errors.email?.message ?? ''}
              refObj={emailProps.ref}
              handleReset={emailVal?.length > 0 ? resetEmail : undefined}
              required
              {...emailProps}
            />
            <div className={styles.notEditableInfo}>
              <p className={styles.notEditableInfoTitle}>{t('phoneNumber')}</p>
              <p
                className={classes([
                  styles.notEditableInfoValue,
                  'phoneNumber',
                ])}
              >
                {mainStore.prettyPhoneNumber(checkoutStore.phoneVal ?? '')}
              </p>
            </div>
          </div>
        </ModalBody>
        <ModalFooter className={styles.recieverForm__footer}>
          <button className="button _primary _med" type="submit">
            {t('apply')}
          </button>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default observer(ModalCheckoutRecieverCD);
